import type { PresenterDepth } from "../domain";
import type { PresenterAudience } from "./audience";
import type { DepthContent } from "./depth";

export type GuidanceSurface =
  | "operations"
  | "incidents"
  | "assets"
  | "phone-detail"
  | "infrastructure"
  | "reports"
  | "placeholder";

export type GuidedField = {
  id: string;
  label: string;
  audience: Record<PresenterAudience, string>;
  depth: DepthContent<string>;
};

export type SurfaceFieldGuide = {
  title: string;
  fields: readonly GuidedField[];
};

export type ResolvedFieldGuidance = {
  id: string;
  label: string;
  audienceExplanation: string;
  depthExplanation: string;
  fallback: boolean;
};

const commonDepth = {
  overview: "Read the outcome first; technical identifiers are intentionally secondary.",
  operations: "Use this field to decide, assign, verify, or document the next safe action.",
  architecture: "Follow the stable record and relationship to inspect provenance without changing access.",
} satisfies Record<PresenterDepth, string>;

function contextualDepth(label: string): DepthContent<string> {
  const fieldName = label.toLocaleLowerCase();
  return {
    overview: `Use ${fieldName} to understand the immediate outcome.`,
    operations: `Use ${fieldName} to choose, verify, or document the next safe action.`,
    architecture: `Trace ${fieldName} to its stable source record and relationships.`,
  };
}

function field(
  id: string,
  label: string,
  audience: Record<PresenterAudience, string>,
  overview: string,
  operations: string,
  architecture: string,
): GuidedField {
  const contextual = contextualDepth(label);
  return {
    id,
    label,
    audience,
    depth: {
      overview: overview === commonDepth.overview ? contextual.overview : overview,
      operations: operations === commonDepth.operations ? contextual.operations : operations,
      architecture: architecture === commonDepth.architecture ? contextual.architecture : architecture,
    },
  };
}

const fallbackAudience: Record<PresenterAudience, string> = {
  "telecom-admin": "Treat this local field as supporting context until its service source is confirmed.",
  "it-staff": "Confirm the visible record, source, and status before using this field for action.",
  executive: "Treat this as demonstration context rather than a measured institutional claim.",
  student: "This local demonstration field needs a plain-language definition before it guides a decision.",
};

export const fieldGuidanceRegistry: Record<GuidanceSurface, SurfaceFieldGuide> = {
  operations: {
    title: "Operating snapshot fields",
    fields: [
      field("campus-status", "Campus status", {
        "telecom-admin": "Shows whether voice, Wi-Fi, and switching are supporting normal campus service.",
        "it-staff": "Summarizes the current service and device queue before deeper triage.",
        executive: "States whether technology is materially interrupting college operations.",
        student: "Explains whether the digital services used for classes and campus life are available.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("affected-population", "People affected", {
        "telecom-admin": "Scopes who may be unable to register phones or reliably reach network services.",
        "it-staff": "Provides an impact estimate for prioritization and escalation.",
        executive: "Frames the population consequence; this remains a fictional operating estimate.",
        student: "Explains how broadly other students and employees may notice the interruption.",
      }, "Treat the number as an impact headline.", "Compare it with service state and response stage.", "Trace the estimate to the incident and service relationships."),
      field("service-health", "Service health", {
        "telecom-admin": "Separates voice, wireless, and switching symptoms so recovery can be verified by service.",
        "it-staff": "Identifies which technical service queue requires investigation.",
        executive: "Shows which college capabilities are degraded without exposing restricted topology.",
        student: "Translates system health into the campus services that may feel slow or unavailable.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("next-action", "Next safe action", {
        "telecom-admin": "Points to the evidence-backed action that protects service continuity.",
        "it-staff": "Identifies the next executable step and its permission boundary.",
        executive: "Shows who is accountable and whether an approval or escalation is needed.",
        student: "Explains what the college technology team is doing next.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
    ],
  },
  incidents: {
    title: "Incident fields",
    fields: [
      field("severity-stage", "Severity and stage", {
        "telecom-admin": "Shows urgency and where the response sits between detection and verified recovery.",
        "it-staff": "Controls queue priority and the expected operational task.",
        executive: "Summarizes business urgency and response progress.",
        student: "Explains how serious the interruption is and whether recovery is underway.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("evidence", "Evidence", {
        "telecom-admin": "Connects symptoms, changes, and service impact before an action is approved.",
        "it-staff": "Provides source, time, confidence, and linkage needed for a working theory.",
        executive: "Demonstrates that the response is based on recorded facts rather than guesswork.",
        student: "Shows that the team checks several signals before making a change.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("response", "Response consequence", {
        "telecom-admin": "Compares restoration, remaining users, timing, and risk before confirmation.",
        "it-staff": "Makes each simulated action and projected technical consequence explicit.",
        executive: "Frames the continuity tradeoff and accountable decision owner.",
        student: "Explains what the proposed fix may improve and what could remain unavailable.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("receipts", "Receipts", {
        "telecom-admin": "Preserves who acted, what changed, and how recovery was verified.",
        "it-staff": "Creates an append-only operational trail for handoff and review.",
        executive: "Provides accountability without claiming delivery to an external system.",
        student: "Shows that important recovery steps are recorded and checked.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
    ],
  },
  assets: {
    title: "Inventory fields",
    fields: [
      field("identity", "Asset identity", {
        "telecom-admin": "Distinguishes the phone or connected endpoint that may affect service.",
        "it-staff": "Provides the tag, type, category, and permitted identifier used for support.",
        executive: "Counts a managed college resource without exposing unnecessary identifiers.",
        student: "Identifies the kind of college-owned device in plain language.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("custody", "Custodian and department", {
        "telecom-admin": "Shows the responsible person or pool for service follow-up.",
        "it-staff": "Resolves the active assignment needed for support and return workflows.",
        executive: "Shows organizational accountability for a college asset.",
        student: "Explains whether a device is assigned or available without exposing restricted details.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("attention", "State and attention", {
        "telecom-admin": "Highlights offline, unverified, or repair states that can affect communications.",
        "it-staff": "Combines lifecycle and verification signals for the working queue.",
        executive: "Summarizes inventory risk and follow-up volume.",
        student: "Explains whether a device is ready, being repaired, or needs a check.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("location", "Location", {
        "telecom-admin": "Places the endpoint in a room so service and jack records can be correlated.",
        "it-staff": "Links the asset record to the room used for dispatch and verification.",
        executive: "Shows which campus area owns the operational impact.",
        student: "Explains where the college device is normally used.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
    ],
  },
  "phone-detail": {
    title: "Phone record fields",
    fields: [
      field("assignment", "Assigned user", {
        "telecom-admin": "Connects a campus line and device to its accountable service recipient.",
        "it-staff": "Resolves the active assignment for support, return, or reassignment.",
        executive: "Shows ownership while role masking protects restricted identity details.",
        student: "Explains who the college phone is intended to support in privacy-safe language.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("line-service", "Line and service", {
        "telecom-admin": "Shows registration and service state for the campus voice endpoint.",
        "it-staff": "Provides the voice-service identifiers permitted by the active role.",
        executive: "Summarizes whether the phone can support normal college communication.",
        student: "Explains whether the campus phone can currently place and receive calls.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("device-health", "Device health", {
        "telecom-admin": "Separates endpoint health from upstream network or carrier impact.",
        "it-staff": "Supports device troubleshooting and replacement decisions.",
        executive: "Shows whether the interruption is isolated or part of a broader service issue.",
        student: "Explains whether the phone itself is working normally.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("network-path", "Network path", {
        "telecom-admin": "Connects the phone through jack, switch port, VLAN, and voice service.",
        "it-staff": "Provides the typed dependency path for root-cause work.",
        executive: "Shows that the impact can be traced without exposing restricted values.",
        student: "Explains that several connected campus systems must work together for the phone to work.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
    ],
  },
  infrastructure: {
    title: "Infrastructure trace fields",
    fields: [
      field("endpoints", "Trace endpoints", {
        "telecom-admin": "Defines the room, service, phone, or person impact being traced.",
        "it-staff": "Sets the source and target records for dependency inspection.",
        executive: "Frames which campus outcome the technical trace explains.",
        student: "Explains where the connection starts and which service experience it reaches.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("path-status", "Path status", {
        "telecom-admin": "Shows where voice or network continuity becomes degraded.",
        "it-staff": "Identifies the first unhealthy hop for investigation.",
        executive: "Summarizes whether the dependency chain is intact.",
        student: "Shows which part of the connection is working or having trouble.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("relationship", "Relationship type", {
        "telecom-admin": "Distinguishes physical termination, switching, VLAN, assignment, and service edges.",
        "it-staff": "Prevents an inferred relationship from being mistaken for a recorded connection.",
        executive: "Demonstrates traceability across accountable systems.",
        student: "Explains how rooms, cables, equipment, and services connect.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("provenance", "Evidence source", {
        "telecom-admin": "States which local record supports each hop before action.",
        "it-staff": "Preserves source and identifier context for verification.",
        executive: "Shows the boundary between recorded evidence and simulation.",
        student: "Explains that the diagram comes from fictional demonstration records.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
    ],
  },
  reports: {
    title: "Report fields",
    fields: [
      field("scope", "Incident scope", {
        "telecom-admin": "Captures service, building, phone, and population impact.",
        "it-staff": "Defines the records included in the generated report.",
        executive: "Summarizes operational consequence and affected college areas.",
        student: "Explains what service was affected and where.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("decision", "Decision", {
        "telecom-admin": "Records the selected response and accountable operator.",
        "it-staff": "Preserves the confirmed branch and its evidence gate.",
        executive: "Shows who authorized the response and why.",
        student: "Explains which recovery approach the team chose.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("verification", "Recovery verification", {
        "telecom-admin": "Confirms phone registration and service restoration rather than relying on an action alone.",
        "it-staff": "Records post-change evidence and remaining impact.",
        executive: "Shows whether service recovery was actually confirmed.",
        student: "Explains how the college checked that the service was working again.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("provenance", "Generated from", {
        "telecom-admin": "Connects the report to incident receipts and stable anchors.",
        "it-staff": "Lists the local records that produced the report.",
        executive: "Clarifies that this is a local demonstration report, not a delivered filing.",
        student: "Explains where the report information came from.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
    ],
  },
  placeholder: {
    title: "Product-area fields",
    fields: [
      field("availability", "Availability", {
        "telecom-admin": "States whether this operating area is active in the first build.",
        "it-staff": "Distinguishes implemented workflow from registered product direction.",
        executive: "Prevents roadmap capability from being mistaken for live functionality.",
        student: "Explains whether this area can be used in the demonstration yet.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("workflow", "Planned workflow", {
        "telecom-admin": "Explains the service-management outcome this area will support.",
        "it-staff": "Identifies the future queue, records, and actions.",
        executive: "States the intended operational value without claiming completion.",
        student: "Explains what this tool area will help the college do.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("boundary", "Data boundary", {
        "telecom-admin": "Keeps inactive integrations and unimplemented records explicit.",
        "it-staff": "States that no external connector is queried.",
        executive: "Separates demonstration scope from production capability.",
        student: "Explains that the page uses fictional local information.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
      field("next-step", "Next useful step", {
        "telecom-admin": "Points back to a working service-impact flow.",
        "it-staff": "Provides a safe route to an implemented task.",
        executive: "Keeps the demonstration focused on verifiable capability.",
        student: "Offers a working page where the topic can be explored now.",
      }, commonDepth.overview, commonDepth.operations, commonDepth.architecture),
    ],
  },
};

export function resolveFieldGuidance(
  surface: GuidanceSurface,
  fieldId: string,
  audience: PresenterAudience,
  depth: PresenterDepth,
): ResolvedFieldGuidance {
  const guide = fieldGuidanceRegistry[surface];
  const guidedField = guide.fields.find((item) => item.id === fieldId);
  if (guidedField) {
    return {
      id: guidedField.id,
      label: guidedField.label,
      audienceExplanation: guidedField.audience[audience],
      depthExplanation: guidedField.depth[depth],
      fallback: false,
    };
  }

  return {
    id: fieldId,
    label: "Context field",
    audienceExplanation: fallbackAudience[audience],
    depthExplanation: contextualDepth(guide.title)[depth],
    fallback: true,
  };
}
